import { computed, reactive, readonly, ref } from 'vue';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { useOutcomeToolsSorApi } from '@/composables/api/outcomeTools/useOutcomeToolsSorApi';
import { outcomeToolsLinkErrorMessage } from '@/composables/api/outcomeTools/outcomeToolsErrorMessage';
import { useAuthStore } from '@/stores/auth';
import { useNotificationStore } from '@/stores/notification';
import { VIEW_OUTCOMETOOLS } from '@/common/permissionConstants';

// Patient id → OutcomeTools client id for every link made through linkPatient,
// shared app-wide so a chart's OutcomeTools card can show a link made from a
// dialog without reloading the patient. Only ever holds links that succeeded.
const linkedPatients = reactive({});
const readonlyLinkedPatients = readonly(linkedPatients);

/**
 * Shared gating state for the OutcomeTools V2 (SOR) patient integration:
 * whether the account is on V2, whether the acting user's practitioner is
 * linked to an OT user, and whether the Add to OutcomeTools affordances
 * should show. Presence of Patient.outcomeToolsId is the per-patient
 * "linked" flag; this composable covers the account/user half of the gate.
 */
export function useOutcomeToolsPatientSync() {
    const authStore = useAuthStore();

    const isSorEnabled = computed(() => !!authStore.account?.outcomeToolsSorEnabled);

    const actingPractitionerOtId = ref(null);
    let practitionerLoadStarted = false;

    // BNSupport agents are issued a stub practitioner with no backing FHIR
    // resource (see BnOTCard), so the lookup is skipped and the affordances
    // stay hidden for them.
    const loadActingPractitioner = async () => {
        if (practitionerLoadStarted) return;
        practitionerLoadStarted = true;

        if (!isSorEnabled.value || authStore.isBnSupportUser) return;
        const practitionerId = authStore.practitioner?.id;
        if (!practitionerId) return;

        const practitionerData = await $httpFhirApi.get(`Practitioner/${practitionerId}`, { errorHandle: false }).catch(() => null);
        actingPractitionerOtId.value = practitionerData?.originalData?.outcomeToolsId ?? null;
    };

    const canLinkPatient = computed(() => isSorEnabled.value && authStore.hasPermission(VIEW_OUTCOMETOOLS) && !!actingPractitionerOtId.value);

    // An OutcomeTools account with no care team roles can't take the care team,
    // and Re-sync can't change that: an OutcomeTools admin has to add the
    // roles. Said here instead of in a sync warning.
    const notifyCareTeamRolesMissing = (report) => {
        if (!report?.careTeam?.rolesMissing) return;
        useNotificationStore().add({
            message: 'The care team was not sent to OutcomeTools\u{2120}: this account has no care team roles set up there. An OutcomeTools administrator can add them in OutcomeTools settings.',
            color: 'warning',
            timeout: 10000,
        });
    };

    // Toast a link/re-sync provisioning report: clean success, or a warning
    // naming what didn't sync — the patient is linked either way (a failed
    // client create rejects before a report exists), never silently degraded.
    const notifyProvisionReport = (report, successMessage = 'Patient added to OutcomeTools\u{2120}') => {
        const notificationStore = useNotificationStore();
        if (!report?.errors?.length) {
            notificationStore.add({ message: successMessage, color: 'success' });
        } else {
            notificationStore.add({
                message: `Patient is linked to OutcomeTools\u{2120}, but some data did not sync: ${report.errors.join(' ')} Use Re-sync on the OutcomeTools card to retry.`,
                color: 'warning',
                timeout: 10000,
            });
        }
        notifyCareTeamRolesMissing(report);
    };

    // Link a patient after a save (the Program and Episode dialogs). Never
    // throws: the save already went through, so a failed link is one error
    // toast, worded from what was saved (`saved: 'Program'` → "Program saved,
    // but…"); `prefix`/`fallback` override it. Resolves the OutcomeTools
    // client id, or null when not linked.
    const linkPatient = async (patientId, { saved = 'Changes', prefix, fallback } = {}) => {
        try {
            const result = await useOutcomeToolsSorApi().linkPatient(patientId);
            if (!result?.clientId) return null;
            linkedPatients[patientId] = result.clientId;
            notifyProvisionReport(result);
            return result.clientId;
        } catch (error) {
            console.error(`OutcomeTools link failed for patient ${patientId} (non-blocking):`, error);
            useNotificationStore().add({
                message: outcomeToolsLinkErrorMessage(error, {
                    prefix: prefix ?? `${saved} saved, but the patient could not be added to OutcomeTools\u{2120}: `,
                    fallback: fallback ?? `${saved} saved, but the patient could not be added to OutcomeTools\u{2120}. Use the OutcomeTools card to try again.`,
                }),
                color: 'error',
            });
            return null;
        }
    };

    // Best-effort after-save syncs: fired on every save of a V2 account's
    // patient data (no permission check — any chart-editing user keeps OT in
    // sync). The server skips unlinked patients, so these never link anyone.
    // Failures degrade to one warning toast; a Fresh save never fails or
    // waits because OutcomeTools is down.
    const notifySyncProblem = () => {
        useNotificationStore().add({
            message: 'Saved, but OutcomeTools\u{2120} could not be updated. Use Re-sync on the OutcomeTools card to retry.',
            color: 'warning',
            timeout: 10000,
        });
    };

    const bestEffortSync = async (call) => {
        if (!isSorEnabled.value) return;
        try {
            const result = await call(useOutcomeToolsSorApi());
            if (result?.errors?.length) {
                notifySyncProblem();
            }
        } catch (error) {
            console.error('OutcomeTools sync failed (non-blocking):', error);
            notifySyncProblem();
        }
    };

    const syncDemographics = (patientId) => bestEffortSync((api) => api.syncPatientDemographics(patientId));
    const syncEpisode = (patientId, episodeOfCareId) => bestEffortSync((api) => api.syncPatientEpisode(patientId, episodeOfCareId));
    const syncEncounter = (patientId, encounterId) => bestEffortSync((api) => api.syncPatientEncounter(patientId, encounterId));
    const syncEncounterDeleted = (patientId, encounterId) => bestEffortSync((api) => api.syncPatientEncounterDeleted(patientId, encounterId));
    const syncDiagnoses = (patientId) => bestEffortSync((api) => api.syncPatientDiagnoses(patientId));
    const syncRelated = (patientId, relationshipId) => bestEffortSync((api) => api.syncPatientRelated(patientId, relationshipId));
    // Only the care team save names a missing role list; the episode and
    // program syncs that also send the team stay quiet about it.
    const syncCareTeam = (patientId) =>
        bestEffortSync(async (api) => {
            const result = await api.syncPatientCareTeam(patientId);
            notifyCareTeamRolesMissing(result);
            return result;
        });

    return {
        isSorEnabled,
        actingPractitionerOtId,
        loadActingPractitioner,
        canLinkPatient,
        notifyProvisionReport,
        linkPatient,
        linkedPatients: readonlyLinkedPatients,
        syncDemographics,
        syncEpisode,
        syncEncounter,
        syncEncounterDeleted,
        syncDiagnoses,
        syncRelated,
        syncCareTeam,
    };
}
